import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { useAssignFilters, AssignFilters as AssignFiltersType } from '@/hooks/assign-sample/useAssignFilters';
import { SampleType } from '@/features/alojamientos/services/sampleTypeService';

interface AssignFiltersProps {
  onFiltersChange: (filters: AssignFiltersType) => void;
  regions: Array<{ id: number; name: string }>;
  localities: Array<{ id: number; name: string }>;
  segments: Array<{ id: string; name: string }>;
  statusOptions: Array<{ id: number; name: string }>;
  sampleTypes: SampleType[];
  onDataReady?: (isReady: boolean) => void;
}

const AssignFilters: React.FC<AssignFiltersProps> = ({
  onFiltersChange,
  regions,
  localities,
  segments,
  statusOptions,
  sampleTypes,
  onDataReady
}) => {
  // Agregar la opción "Sin Muestra" al inicio de la lista
  const allSampleTypes = [
    { id: 0, name: "Sin Muestra", accommodationCount: 0 },
    ...sampleTypes
  ];

  useEffect(() => {
    if (
      regions.length > 0 &&
      localities.length > 0 &&
      segments.length > 0 &&
      statusOptions.length > 0 &&
      sampleTypes.length > 0
    ) {
      requestAnimationFrame(() => {
        onDataReady?.(true);
      });
    }
  }, [regions, localities, segments, statusOptions, sampleTypes]);
  // Efecto para verificar cuando todos los datos están listos
  
  const {
    filters,
    activeFilters,
    handleFilterChange,
    removeFilter,
    clearFilters
  } = useAssignFilters({
    regions,
    localities,
    segments,
    statusOptions,
    sampleTypes
  });

  // Notificar cambios en los filtros al componente padre solo cuando todos los datos estén cargados
  
  // Función para obtener el nombre basado en el tipo de filtro y el ID
  const getFilterName = (key: string, value: string): string => {
    switch (key) {
      case 'sampletype':
        return allSampleTypes.find(s => s.id.toString() === value)?.name || value;
      case 'region':
        return regions.find(r => r.id.toString() === value)?.name || value;
      case 'localidad':
        return localities.find(l => l.id.toString() === value)?.name || value;
      case 'segmento':
        return segments.find(s => s.id === value)?.name || value;
      case 'estado':
        return statusOptions.find(s => s.id.toString() === value)?.name || value;
      default:
        return value;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          {activeFilters.length > 0 && (
            <Button variant="outline" size="sm" onClick={clearFilters}>
              Limpiar Filtros ({activeFilters.length})
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">

          {/* Sample Type Filter */}
          <div>
            <label className="text-sm font-medium mb-2 block">Tipo de Muestra</label>
            <Select
              value={filters.sampletype || undefined}
              onValueChange={(value) => handleFilterChange('sampletype', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar..." />
              </SelectTrigger>
              <SelectContent>
                {allSampleTypes.map((sampleType) => (
                  <SelectItem key={sampleType.id} value={sampleType.id.toString()}>
                    {sampleType.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Región Filter */}
          <div>
            <label className="text-sm font-medium mb-2 block">Región</label>
            <Select
              value={filters.region || undefined}
              onValueChange={(value) => handleFilterChange('region', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar..." />
              </SelectTrigger>
              <SelectContent>
                {regions.map((region) => (
                  <SelectItem key={region.id} value={region.id.toString()}>
                    {region.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Localidad Filter */}
          <div>
            <label className="text-sm font-medium mb-2 block">Localidad</label>
            <Select
              value={filters.localidad || undefined}
              onValueChange={(value) => handleFilterChange('localidad', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar..." />
              </SelectTrigger>
              <SelectContent>
                {localities.map((locality) => (
                  <SelectItem key={locality.id} value={locality.id.toString()}>
                    {locality.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Segmento Filter */}
          <div>
            <label className="text-sm font-medium mb-2 block">Segmento</label>
            <Select
              value={filters.segmento || undefined}
              onValueChange={(value) => handleFilterChange('segmento', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar..." />
              </SelectTrigger>
              <SelectContent>
                {segments.map((segment) => (
                  <SelectItem key={segment.id} value={segment.id}>
                    {segment.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Estado Filter */}
          <div>
            <label className="text-sm font-medium mb-2 block">Estado</label>
            <Select
              value={filters.estado || undefined}
              onValueChange={(value) => handleFilterChange('estado', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar..." />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((status) => (
                  <SelectItem key={status.id} value={status.id.toString()}>
                    {status.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

        </div>

        {/* Active Filters Display */}
        {activeFilters.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {activeFilters.map((filter, index) => {
              const [key, value] = filter.split(': ');
              const filterKey = key.toLowerCase() as keyof AssignFiltersType;
              const displayName = getFilterName(filterKey, value);
              return (
                <Badge key={index} variant="secondary" className="flex items-center gap-1">
                  {`${key}: ${displayName}`}
                  <X 
                    size={14} 
                    className="cursor-pointer hover:text-red-500" 
                    onClick={() => removeFilter(filterKey)}
                  />
                </Badge>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AssignFilters;
